import { useCallback, useEffect, useRef, useState } from 'react'

export interface UseDropdownKeyboardNavOptions {
  /** Number of selectable items currently displayed. */
  itemCount: number
  /** Called when the user confirms selection (Enter or Tab) on the highlighted item. */
  onSelect: (index: number) => void
  /** Whether the dropdown list is currently visible. */
  isOpen: boolean
  /** Optional: called on Escape. Useful for closing the dropdown programmatically. */
  onClose?: () => void
  /** ID prefix for option elements, used for aria-activedescendant. */
  idPrefix?: string
}

/**
 * Keyboard navigation for searchable dropdown components.
 *
 * Handles ArrowUp/Down (highlight), Enter (select + prevent form submit),
 * Escape (close), and Tab (select-and-advance or close).
 *
 * Returns:
 * - `highlightedIndex`: current keyboard-highlighted item (-1 = none)
 * - `handleKeyDown`: attach to the search `<input>` onKeyDown
 * - `getOptionId`: generates a stable option ID for ARIA
 * - `activeDescendant`: the ID of the currently highlighted option (or undefined)
 * - `listboxId`: stable ID for the listbox element
 */
export function useDropdownKeyboardNav({
  itemCount,
  onSelect,
  isOpen,
  onClose,
  idPrefix = 'dkn',
}: UseDropdownKeyboardNavOptions) {
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  // Keep onSelect/onClose in refs to avoid stale closures in handleKeyDown
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // Reset highlight when item list changes or dropdown closes
  useEffect(() => {
    setHighlightedIndex(-1)
  }, [itemCount, isOpen])

  const listboxId = `${idPrefix}-listbox`

  const getOptionId = useCallback(
    (index: number) => `${idPrefix}-option-${index}`,
    [idPrefix]
  )

  const activeDescendant =
    isOpen && highlightedIndex >= 0
      ? getOptionId(highlightedIndex)
      : undefined

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen || itemCount === 0) {
        // Even when closed, block Enter from submitting the form if it just closed
        if (e.key === 'Enter') {
          // Allow normal behavior when dropdown is closed
        }
        return
      }

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault()
          setHighlightedIndex(prev =>
            prev >= itemCount - 1 ? 0 : prev + 1
          )
          break
        }
        case 'ArrowUp': {
          e.preventDefault()
          setHighlightedIndex(prev =>
            prev <= 0 ? itemCount - 1 : prev - 1
          )
          break
        }
        case 'Enter': {
          e.preventDefault() // Always prevent form submission when dropdown is open
          if (highlightedIndex >= 0 && highlightedIndex < itemCount) {
            onSelectRef.current(highlightedIndex)
            setHighlightedIndex(-1)
          }
          break
        }
        case 'Escape': {
          e.preventDefault()
          setHighlightedIndex(-1)
          onCloseRef.current?.()
          break
        }
        case 'Tab': {
          // Select the highlighted item before moving focus
          if (highlightedIndex >= 0 && highlightedIndex < itemCount) {
            onSelectRef.current(highlightedIndex)
            setHighlightedIndex(-1)
          }
          // Do NOT preventDefault — let the browser move focus naturally
          break
        }
        default:
          break
      }
    },
    [isOpen, itemCount, highlightedIndex]
  )

  // Scroll the highlighted option into view
  useEffect(() => {
    if (highlightedIndex < 0 || !isOpen) return
    const el = document.getElementById(getOptionId(highlightedIndex))
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex, isOpen, getOptionId])

  return {
    highlightedIndex,
    handleKeyDown,
    getOptionId,
    activeDescendant,
    listboxId,
  }
}
